import {
  PoolRole,
  Report_ElementType,
  Report_RoundingValue,
  useGetInstitutionSignatureStatusQuery,
  useGetRemittanceHistoriesByClientAndRoleQuery,
  useGetSavedLoanGroupsQuery,
} from "@/services/graphql/reactQuery";
import { createReportingElement } from "@/shared/components/elements/util";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { PaxTextLink } from "@/shared/components/PaxTextLink";
import { useTableSort } from "@/shared/hooks/useTableSort";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Tooltip, Typography, useTranslation } from "@ncino/pax-ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ReactNode, useEffect, useMemo, useState } from "react";
import z from "zod";
import {
  LoanPoolItem,
  ManageAnchorTabs,
  PurchasedPool,
  PurchasePoolEntry,
  RemittanceHistory,
  RowActionMap,
  SoldPool,
  SoldPoolEntry,
} from "./-internal/dataTypes";
import { ImportSoldPoolWorkflowModal } from "./-internal/importSoldPoolWorkflowModal";
import { useManageRouteContext } from "./-internal/ManageContext";
import { getPurchasedPoolHeaders, getSoldPoolHeaders } from "./-internal/tableHeaders";
import {
  getAvailableManagedPoolActions,
  poolsToElementData,
  queryStatusToReportStatus,
  RemittanceStatus,
  useManagePurchasedPools,
  useManageSoldPools,
} from "./-internal/util";
import { usePaxPagination } from "./-internal/ValidationStep/boaTable/usePaxPagination";
import { useIsPermissionGroupMemberQuery } from "@/services/auth/permissions";

export const Route = createFileRoute("/manage/")({
  component: RouteComponent,
  validateSearch: z.object({
    tab: ManageAnchorTabs.catch("sold"),
  }),
  loader: ({ context }): void => {
    // Root's beforeLoad already ensured this — safe to read synchronously
    const { institutionId, portfolioId } = context;
    if (!institutionId) throw new Error("Institution is undefined for user");
    if (!portfolioId) throw new Error("Portfolio is undefined for user");
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation("manage");
  const locale = i18n.language.replace("_", "-");
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const {
    addPoolTab,
    setRemittancePoolData,
    setRemitWorkflowModalIsOpen,
    setIsActionModalOpen,
    setAnchorTab,
    setLoadDataPoolItem,
    canCreateRemittance,
  } = useManageRouteContext();
  const [importSoldPoolModalIsOpen, setImportSoldPoolModalIsOpen] = useState(false);
  const purchasedPools = useManagePurchasedPools();
  const soldPools = useManageSoldPools();
  const { portfolioId, institutionId } = Route.useRouteContext();
  const purchasedRemittancePools = useGetRemittanceHistoriesByClientAndRoleQuery({
    clientId: institutionId,
    role: PoolRole.Buyer,
  });
  const soldRemittancePools = useGetRemittanceHistoriesByClientAndRoleQuery({
    clientId: institutionId,
    role: PoolRole.Seller,
  });
  const {
    data: savedGroupsData,
    isLoading: savedGroupsLoading,
    isError: savedGroupsError,
  } = useGetSavedLoanGroupsQuery({ portfolioId: portfolioId! }, { enabled: portfolioId != null });
  const hasSavedGroups =
    (savedGroupsData?.report_getLoanGroups?.filter((g) => (g?.recordCount ?? 0) > 0).length ?? 0) >
    0;
  const {
    data: canImportPool,
    isLoading: importPoolIsLoading,
    isError: importPoolError,
  } = useIsPermissionGroupMemberQuery("importSavedGroupAsPool");
  const {
    data: orderForm,
    isLoading: orderFormIsLoading,
    isError: orderFormError,
  } = useGetInstitutionSignatureStatusQuery({ institutionId: institutionId });

  const importDisabled =
    orderFormIsLoading ||
    orderFormError ||
    !orderForm?.SignatureService_getInstitutionSignatureStatus.signed ||
    importPoolIsLoading ||
    importPoolError ||
    !canImportPool ||
    savedGroupsLoading ||
    savedGroupsError ||
    !hasSavedGroups;
  const importTooltip =
    savedGroupsLoading || importPoolIsLoading || orderFormIsLoading
      ? ""
      : savedGroupsError || importPoolError || orderFormError
        ? t("managePoolsPage.importPoolErrorTooltip")
        : !orderForm?.SignatureService_getInstitutionSignatureStatus.signed
          ? t("managePoolsPage.importPoolNoOrderForm")
          : !canImportPool
            ? t("managePoolsPage.importPoolNoImportPermissions")
            : !hasSavedGroups
              ? t("managePoolsPage.importPoolDisabledTooltip")
              : t("managePoolsPage.importPoolEnabledTooltip");
  const managePoolsData = {
    soldPools,
    purchasedPools,
    soldRemittancePools,
    purchasedRemittancePools,
  };

  useEffect(() => {
    setAnchorTab(tab);
  }, []);

  const openTab = (loanPoolItem: LoanPoolItem, active?: boolean) => {
    addPoolTab(loanPoolItem, active);
    if (active) {
      navigate({
        to: "/manage/$poolId",
        params: { poolId: loanPoolItem.tabId },
        search: { tab: "analytics", report: "overview" },
      });
    }
  };

  const rowActionMap: RowActionMap = {
    openModal: (loanPool?: LoanPoolItem) => {
      if (loanPool) {
        setRemittancePoolData({
          clientId: institutionId,
          poolId: loanPool.id,
          reportingPeriod: loanPool.reportingPeriod,
          status: loanPool.status,
          view: loanPool.origin == "Sold" ? "sold" : "purchased",
        });
        setRemitWorkflowModalIsOpen(true);
      } else setIsActionModalOpen(true);
    },
    openRemittanceModal: (loanPool: LoanPoolItem) => {
      setRemittancePoolData({
        clientId: institutionId,
        poolId: loanPool.id,
        reportingPeriod: loanPool.reportingPeriod,
        status: loanPool.status,
        view: loanPool.origin == "Sold" ? "sold" : "purchased",
      });
      setRemitWorkflowModalIsOpen(true);
    },
    openTab,
    openLoadTransactionDataModal: (loanPool: LoanPoolItem) => {
      setLoadDataPoolItem(loanPool);
    },
  };

  const getPoolBaseFromSoldPool = (pool: SoldPool) => {
    const status = RemittanceStatus.ReadyToReview;
    return {
      poolId: Number(pool.id),
      poolName: pool.poolNumber ?? "-",
      loanType: pool.poolType ?? "-",
      numLoans: pool.loanCount ?? 0,
      reportingPeriod: "-",
      status,
      actions: getAvailableManagedPoolActions(status, true, rowActionMap),
      price: pool.listing?.price ?? 0,
    };
  };

  const getPoolBaseFromPurchasedPool = (participant: PurchasedPool) => {
    const status = RemittanceStatus.ReadyToReview;
    return {
      poolId: Number(participant.pool?.id),
      poolName: participant.pool?.poolNumber ?? "-",
      loanType: participant.pool?.poolType ?? "-",
      numLoans: participant.pool?.loanCount ?? 0,
      reportingPeriod: "-",
      status,
      actions: getAvailableManagedPoolActions(status, false, rowActionMap),
      price: participant.pool?.listing?.price ?? 0,
    };
  };

  const getPoolBaseFromRemittanceHistory = (remittance: RemittanceHistory, isSeller: boolean) => {
    const status = remittance.status;
    return {
      poolId: Number(remittance.pool?.id),
      poolName: remittance.pool?.poolNumber ?? "-",
      loanType: remittance.pool?.poolType ?? "-",
      numLoans: remittance.pool?.loanCount ?? 0,
      reportingPeriod: remittance.reportingPeriod ?? "-",
      status,
      actions: getAvailableManagedPoolActions(status, isSeller, rowActionMap),
      price: remittance.pool?.listing?.price ?? 0,
    };
  };

  const purchasedPoolData = useMemo<Partial<PurchasePoolEntry>[]>(() => {
    if (canCreateRemittance) {
      return (managePoolsData?.purchasedRemittancePools.data?.remittanceHistories ?? []).map(
        (p) => ({
          ...getPoolBaseFromRemittanceHistory(p, false),
          purchaseDate: p.pool?.listing?.soldDate ?? "",
          seller: p.pool?.client?.companyName ?? "-",
          origin: "Purchased" as const,
        })
      );
    }
    return (managePoolsData?.purchasedPools.data ?? []).map((p) => ({
      ...getPoolBaseFromPurchasedPool(p),
      purchaseDate: p.pool?.listing?.soldDate ?? "",
      seller: p.pool?.client?.companyName ?? "-",
      origin: "Purchased" as const,
    }));
  }, [managePoolsData]);

  const soldPoolData = useMemo(() => {
    if (canCreateRemittance) {
      return (managePoolsData?.soldRemittancePools.data?.remittanceHistories ?? [])
        .filter((p) => p.pool?.participants && p.pool?.participants.length > 1)
        .map((p) => ({
          ...getPoolBaseFromRemittanceHistory(p, true),
          sellDate: p.pool?.listing?.soldDate ?? "",
          buyer:
            p.pool?.participants?.find((participant) => !participant.isSeller)?.buyer
              ?.companyName ?? "-",
          origin: "Sold" as const,
        }));
    }
    return (managePoolsData?.soldPools.data ?? [])
      .filter((p) => p.participants && p.participants.length > 1)
      .map((p) => ({
        ...getPoolBaseFromSoldPool(p),
        sellDate: p.listing?.soldDate ?? "",
        buyer:
          p.participants?.find((participant) => !participant.isSeller)?.buyer?.companyName ?? "-",
        origin: "Sold" as const,
      }));
  }, [managePoolsData]);

  // TODO: Have some way to specify date locale
  const purchasedPoolHeaders = getPurchasedPoolHeaders(t, locale, openTab);
  const soldPoolHeaders = getSoldPoolHeaders(t, locale, openTab);

  const {
    sortedData: sortedPurchasedPoolData,
    sortColumnIndex: purchasedSortColumnIndex,
    sortDirection: purchasedDirection,
    handleSort: handlePurchasedPoolSort,
  } = useTableSort(purchasedPoolData, purchasedPoolHeaders, "poolName");

  const {
    sortedData: sortedSoldPoolData,
    sortColumnIndex: soldSortColumnIndex,
    sortDirection: soldDirection,
    handleSort: handleSoldPoolSort,
  } = useTableSort(soldPoolData, soldPoolHeaders, "poolName");

  const { pagination: purchasedPagination } = usePaxPagination(sortedPurchasedPoolData.length);
  const { pagination: soldPagination } = usePaxPagination(sortedSoldPoolData.length);

  const commonProps = {
    showIndex: false,
    elevated: false,
    tableHeaderVariant: "dark",
    borderedSides: true,
    tableSx: { borderRadius: 0, borderRight: "none", borderLeft: "none" },
    rowHeight: 73,
    settings: {
      dollarPrecision: 1,
      dollarRounding: Report_RoundingValue.Thousands,
    },
  };

  return (
    <PageContentContainer>
      <ImportSoldPoolWorkflowModal
        isOpen={importSoldPoolModalIsOpen}
        onClose={() => setImportSoldPoolModalIsOpen(false)}
      />
      {tab === "sold"
        ? createReportingElement(Report_ElementType.List, {
            props: {
              ...commonProps,
              ...(soldPoolData.length > 0 && { pagination: soldPagination }),
              isLoading: soldPools.isLoading,
              isEmpty: soldPoolData.length === 0,
              elementData: {
                ...poolsToElementData<SoldPoolEntry>(
                  soldPoolData as SoldPoolEntry[],
                  soldPoolHeaders
                ),
                status: canCreateRemittance
                  ? queryStatusToReportStatus(soldRemittancePools.status)
                  : soldPools.reportStatus,
              },
              sortColumnIndex: soldSortColumnIndex,
              sortDirection: soldDirection,
              onSortChange: handleSoldPoolSort,
              customHeader: (
                <ManagePageTableHeader
                  label={t("managePoolsPage.soldPoolsTab")}
                  onImportClick={() => setImportSoldPoolModalIsOpen(true)}
                  importDisabled={importDisabled}
                  importTooltip={importTooltip}
                />
              ),
              emptyStateTitle: t("soldPoolSection.emptyStateMessage"),
              emptyStateAction: (
                <PaxTextLink
                  label={t("soldPoolSection.emptyStateHrefText")}
                  onClick={() => navigate({ to: "/sell" })}
                />
              ),
            },
            onErrorAction: soldPools.refetch,
            key: "manage-sold-pools",
          })
        : createReportingElement(Report_ElementType.List, {
            props: {
              ...commonProps,
              ...(purchasedPoolData.length > 0 && { pagination: purchasedPagination }),
              isLoading: purchasedPools.isLoading,
              isEmpty: purchasedPoolData.length === 0,
              elementData: {
                ...poolsToElementData<PurchasePoolEntry>(
                  purchasedPoolData as PurchasePoolEntry[],
                  purchasedPoolHeaders
                ),
                status: canCreateRemittance
                  ? queryStatusToReportStatus(purchasedRemittancePools.status)
                  : purchasedPools.reportStatus,
              },
              sortColumnIndex: purchasedSortColumnIndex,
              sortDirection: purchasedDirection,
              onSortChange: handlePurchasedPoolSort,
              customHeader: (
                <ManagePageTableHeader label={t("managePoolsPage.purchasedPoolsTab")} />
              ),
              emptyStateTitle: t("purchasedPoolSection.emptyStateMessage"),
              emptyStateAction: (
                <PaxTextLink
                  label={t("purchasedPoolSection.emptyStateHrefText")}
                  onClick={() => navigate({ to: "/buy" })}
                />
              ),
            },
            onErrorAction: purchasedPools.refetch,
            key: "manage-purchased-pools",
          })}
    </PageContentContainer>
  );
}

function ManagePageTableHeader({
  label,
  onImportClick,
  importDisabled,
  importTooltip,
}: {
  label: string;
  onImportClick?: () => void;
  importDisabled?: boolean;
  importTooltip?: string;
}): ReactNode {
  const { t } = useTranslation("manage");
  return (
    <Box
      py={2}
      px={2}
      sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
    >
      <Typography variant="h5">{label}</Typography>
      {onImportClick && (
        <Tooltip title={importTooltip ?? ""}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onImportClick}
              disabled={importDisabled}
              sx={{ px: 3, py: 1.5, mt: 1, mr: 1 }}
            >
              {t("managePoolsPage.importPoolButtonText")}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}
